import { canonicalString, readSession } from './contract';
import { publishDesktopState } from './database';
import { loadCompanySnapshot } from './loadSnapshot';

const CONFIG_PATH = /(?:^|\/)(?:empresa|chatbot|chatbotanswers|grupo-produto|produto|produto-tamanho|tamanho|grupo-tamanhos|forma-pagamento-empresa|formas-pagamento|horario)(?:\/|$)/;
let invalidate = () => {};

/** Reuses the existing notification connection without touching order handlers. */
export function bindDesktopConfigurationEvents(socket, companyId) {
  const changed = () => invalidate();
  const events = [
    `configs-${companyId}`, `configs-groups-${companyId}`, `configs-chatbot-${companyId}`,
    `configs-chatbot-answer-${companyId}`, `configs-company-${companyId}`,
    `configs-payment-methods-${companyId}`, `configs-schedules-${companyId}`,
    // Products have generic CRUD events without configs-* equivalents.
    ...['create', 'update', 'delete'].flatMap((type) =>
      ['product', 'products', 'group', 'chatbot', 'paymentMethod', 'schedule'].map((entity) => `${type}-${entity}-${companyId}`)),
  ];
  events.forEach((event) => socket.on(event, changed));
  socket.on('connect', changed);
  return () => {
    events.forEach((event) => socket.off(event, changed));
    socket.off('connect', changed);
  };
}

export function startDesktopSync(api) {
  if (typeof window === 'undefined' || !window.indexedDB || window.__menucheffDesktopSync) return;
  window.__menucheffDesktopSync = true;
  let identity = '';
  let generation = 0;
  let dirty = true;
  let running = false;
  let timer;

  function report(error) {
    console.error('MenuCheff Desktop: último snapshot válido preservado.', error);
  }

  async function run() {
    if (running || !dirty) return;
    const session = readSession();
    dirty = false;
    if (!session) return;
    const currentGeneration = generation;
    running = true;
    try {
      const snapshot = await loadCompanySnapshot(api, session);
      // Discard a load if login changed or an entity changed during the load.
      if (generation === currentGeneration && !dirty && canonicalString(readSession()) === canonicalString(session)) {
        await publishDesktopState(session, snapshot);
      }
    } catch (error) {
      report(error);
    } finally {
      running = false;
      if (dirty) schedule();
    }
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(run, 250);
  }

  function reconcileSession() {
    const session = readSession();
    const nextIdentity = canonicalString(session);
    if (nextIdentity === identity) return;
    identity = nextIdentity;
    generation += 1;
    dirty = true;
    // Session deletion must not wait for a network request or snapshot build.
    publishDesktopState(session).catch(report);
    schedule();
  }

  invalidate = () => { generation += 1; dirty = true; schedule(); };
  window.addEventListener('menucheff:storage-changed', reconcileSession);
  window.addEventListener('storage', reconcileSession);
  window.addEventListener('focus', () => { reconcileSession(); invalidate(); });
  window.addEventListener('online', invalidate);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) { reconcileSession(); invalidate(); }
  });
  // Catch property assignments, external storage changes and transient DB failures.
  window.setInterval(() => {
    reconcileSession();
    publishDesktopState(readSession()).catch(report);
  }, 1000);
  // Recover events missed during disconnection or mutations without socket broadcasts.
  window.setInterval(invalidate, 60000);
  api.interceptors.response.use((response) => {
    const method = (response.config?.method || 'get').toLowerCase();
    const path = response.config?.url || '';
    const isReadRoute = /\/(?:listar[^/]*|find|encontrar|maisPedidos)(?:\/|$)/.test(path);
    if (!['get', 'head', 'options'].includes(method) && !isReadRoute && CONFIG_PATH.test(path)) invalidate();
    return response;
  });
  reconcileSession();
}
